import { FunctionComponent, useRef, useState } from "react";
import "remirror/styles/all.css";
import InfoModal from "@components/core/modals/InfoModal";

import { MAX_SIGNATURE_MAIL_SIZE } from "@/utils/Constant";
import { EditorComponent, Remirror, useRemirror } from "@remirror/react";
import { useTranslation } from "react-i18next";
import {
  BoldExtension,
  ImageExtension,
  ItalicExtension,
  LinkExtension,
  UnderlineExtension,
} from "remirror/extensions";
import { RemirrorEventListenerProps } from "remirror/extensions/dist/_tsup-dts-rollup";
import CustomToolbar from "@components/core/inputs/Remirror/CustomToolbar";

interface RemirrorFieldProps {
  label: string;
  placeholder: string;
  showIcon?: boolean;
  initialValue: string;
  setSignature: (signature: string) => void;
  className?: string;
  onUserAction?: () => void;
}

const RemirrorField: FunctionComponent<RemirrorFieldProps> = ({
  label,
  placeholder,
  showIcon = false,
  initialValue,
  setSignature,
  className = "",
  onUserAction,
}) => {
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false);

  const previousContent = useRef("");
  const hasFocus = useRef(false);

  const { manager, state, setState } = useRemirror({
    extensions: () => [
      new BoldExtension({}),
      new ImageExtension({}),
      new ItalicExtension(),
      new UnderlineExtension(),
      new LinkExtension({ autoLink: true }),
    ],
    content: initialValue,
    stringHandler: "html",
  });

  const handleChange = (
    parameter: RemirrorEventListenerProps<Remirror.Extensions>,
  ) => {
    const htmlContent = parameter.helpers.getHTML(parameter.state); // Retrieve editor content as HTML

    if (htmlContent.length < MAX_SIGNATURE_MAIL_SIZE) {
      setSignature(htmlContent);
      setIsError(false);

      if (hasFocus.current && previousContent.current !== htmlContent) {
        onUserAction?.();
      }
      previousContent.current = htmlContent;
    } else {
      setIsError(true);
    }
  };

  return (
    <div
      className={`${className} remirror-theme`}
      style={{ position: "relative" }}
    >
      <label
        className="mt-8 d-block text-dark mb-8"
        data-test="input-label"
        data-test-for={label}
        data-alt={label}
      >
        {label}
        &nbsp;
        {showIcon && <InfoModal value="signatureMail" info />}
      </label>
      <Remirror
        manager={manager}
        initialContent={state}
        placeholder={placeholder}
        onChange={(parameter) => {
          setState(parameter.state);
          handleChange(parameter);
        }}
        onFocus={() => (hasFocus.current = true)}
        onBlur={() => (hasFocus.current = false)}
      >
        <CustomToolbar />
        <EditorComponent />
      </Remirror>
      {isError && (
        <div className="text-danger">
          {t("signature.accordion.parametersForm.signature.error")}
        </div>
      )}
    </div>
  );
};

export default RemirrorField;
